import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { BookOpen, MessageCircle, FileText, Sun, User } from 'lucide-react'
import { InstallPrompt } from '@/components/pwa/InstallPrompt'
import { cn } from '@/lib/utils'
import { LegalNotice } from '@/components/legal/LegalNotice'

/**
 * Оболочка приложения.
 *
 * На мобильном браузере — нижний таб-бар как в приложении; на десктопе —
 * боковая панель, потому что растянутый на 1920px таб-бар снизу выглядит
 * ровно как перенесённое мобильное приложение, а веб должен быть вебом.
 */

const TABS = [
  { to: '/lk/today', label: 'Сегодня', icon: Sun },
  { to: '/lk/bible', label: 'Библия', icon: BookOpen },
  { to: '/lk/chat', label: 'Чат', icon: MessageCircle },
  { to: '/lk/notes', label: 'Заметки', icon: FileText },
  { to: '/lk/profile', label: 'Профиль', icon: User },
] as const

/**
 * Маршруты, на которых нижняя навигация мешает: экран переписки занимает
 * весь экран, а поле ввода прижато к низу.
 */
const HIDE_TAB_BAR = [
  /^\/lk\/chat\/[^/]+/,
  /^\/lk\/notes\/(editor|[^/]+)$/,
  /^\/lk\/bible\/[^/]+\/[^/]+\/\d+/,
]

export function AppShell() {
  const { pathname } = useLocation()
  const hideTabBar = HIDE_TAB_BAR.some((re) => re.test(pathname))

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-background">
      {/* Десктопная боковая панель */}
      <nav className="lc-no-print hidden w-[260px] shrink-0 flex-col border-r border-border bg-surface md:flex">
        {/* Логотип уводит на лендинг: он живёт на «/», ЛК — на «/lk» */}
        <div className="flex h-16 items-center px-6">
          <Link to="/" className="text-lg font-bold text-primary">
            LightChat
          </Link>
        </div>
        <div className="flex flex-1 flex-col gap-1 px-3">
          {TABS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-card px-3 py-3 text-base font-medium transition-colors',
                  isActive ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-background',
                )
              }
            >
              <Icon size={22} />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <LegalNotice />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>

        {/* Мобильный таб-бар */}
        {!hideTabBar ? (
          <nav
            className={cn(
              'lc-no-print flex shrink-0 border-t border-border bg-surface md:hidden',
              'pb-[env(safe-area-inset-bottom)]',
            )}
          >
            {TABS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium',
                    isActive ? 'text-primary' : 'text-muted',
                  )
                }
              >
                <Icon size={22} />
                {label}
              </NavLink>
            ))}
          </nav>
        ) : null}
      </div>

      {/* Предложение установить приложение — только внутри кабинета */}
      <InstallPrompt />
    </div>
  )
}
